import React, { useContext, useEffect, useState } from 'react';
import { IonGrid, IonRow, IonCol, IonCard, IonLabel, IonItem, IonInput, IonButtons, IonButton, IonIcon, IonItemDivider, IonTitle, IonCheckbox, IonPopover, IonReorderGroup, IonReorder, IonCardHeader, IonCardContent, IonCardTitle, IonModal, IonFooter, IonToolbar, IonContent, IonList, IonToast } from '@ionic/react';
import { InputMode, Question, QuestionOption } from 'models/questionnaire.model';
import { AppContext } from 'context/app.context';
import { QuestionnaireActionTypes } from 'reducers/questionnaire.reducer';
import { updateQuestion, deleteQuestionOption, createQuestionOption, reorderQuestionOptions } from 'misc/phqHTTP';
import { addCircle, closeCircleOutline, createOutline, ellipsisHorizontalOutline } from 'ionicons/icons';
import ContinueTo from './ContinueTo';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { UIElementEnum } from 'models/ui-element.enum';
import { HttpStatusCodeEnum } from 'models/http-status-code.enum';
import 'jsoneditor-react/es/editor.min.css';
import { JsonEditor } from 'jsoneditor-react';
import { InputModeEnum } from 'models/input-mode.enum';
import { useHotkeys } from 'react-hotkeys-hook';
import HOT_KEYS from 'misc/hot-keys';

export class ManualOptions {
    QuestionsList = [] as QuestionOption[];
}

const PHQProperties = React.memo(
    (props: { selectedItem: Question }) => {
        const { updateQuestionnaire, questionnaire, helpMode } = useContext(AppContext);
        const [paraValue, setParaValue] = useState(props.selectedItem.Label);
        const [showPopover, setShowPopover] = useState<{ open: boolean, questionOption: QuestionOption, event: Event | undefined }>({
            open: false,
            questionOption: new QuestionOption(),
            event: undefined,
        });
        const [showMetaModal, setShowMetaModal] = useState(false);
        const [showJsonEditor, setShowJsonEditor] = useState(true);
        const [parsedMeta, updateParsedMeta] = useState(JSON.parse(props.selectedItem.Meta || "{}"));
        const [inputTypePopoverState, setShowInputTypePopover] = useState({ showPopover: false, event: undefined });
        const [showToast, setShowToast] = useState(false);
        const [toastMessage, setToastMessage] = useState("Copied!");

        const sectionColSpan = (questionnaire?.PageSelected?.Sections || [])
            .find(s => s.Questions?.includes(props.selectedItem))?.Cols;

        useHotkeys(HOT_KEYS.CopyMeta, () => {
            console.log('copy');
            navigator.clipboard.writeText(JSON.stringify(parsedMeta))
            .then(() => {
                setToastMessage("Copied to clipboard!");
                setShowToast(true);
            });
        }, { enabled: showMetaModal });

        useHotkeys(HOT_KEYS.PasteMeta, () => {
            console.log('paste');
            navigator.clipboard.readText()
            .then(val => {
                setToastMessage("Pasted from clipboard!");
                setShowToast(true);
                updateParsedMeta(JSON.parse(val));

                //force editor re-render to display pasted json
                setShowJsonEditor(false);
                setTimeout(() => setShowJsonEditor(true));
            })
        }, { enabled: showMetaModal });

        const updateMeta = () => {
            const stringMeta = JSON.stringify(parsedMeta);
            if(props.selectedItem.Meta !== stringMeta) {
                const q = props.selectedItem;
                q.Meta = stringMeta;
                updateQuestionCallback(q);
            }
            setShowMetaModal(false);
        }

        const onCancelMetaUpdate = () => {
            updateParsedMeta(JSON.parse(props.selectedItem.Meta || "{}"));
            setShowMetaModal(false);
        }

        const updateInputType = (val: InputMode) => {
            const q = props.selectedItem;
            q.InputMode = val;
            updateQuestionCallback(q);
            setShowInputTypePopover({ showPopover: false, event: undefined });
        }

        const updateContinueTo = (e: any) => {
            updateSelectedQuestion(e);
        }

        const updateCheckbox = (e: any) => {
            const payload = { 'target': { 'value': e.detail.checked, 'id': e.target!.id } }
            updateSelectedQuestion(payload)
        }

        const updateColSize = ({target}: any) => {
            const question = props.selectedItem as any;
            const { id: propName, value } = target;
            question[propName] = +value;
            updateQuestionCallback(question)
        }

        const updateSelectedQuestion = (e: any) => {
            const question = props.selectedItem as any;
            if (e.target.id) {
                const fieldName = e.target.id;
                const value = e.target.value;
                if(value !== "") {
                    question[fieldName] = value;
                    updateQuestionCallback(question)
                }
                else {
                    question[fieldName] = question.Label;
                    updateQuestionnaire(QuestionnaireActionTypes.UPDATE_QUESTION, {value: question})
                }
            }
        }

        const updateQuestionCallback = (question: Question) => {
            updateQuestion(question).then(res => {
                if (res && res.data && res.data.result) {
                    const payload = {
                        value: question
                    }
                    updateQuestionnaire(QuestionnaireActionTypes.UPDATE_QUESTION, payload);
                }
            })
        }

        const updateSelectedQuestionParagraph = () => {
            if (paraValue !== props.selectedItem.Label) {
                props.selectedItem.Label = paraValue;
                updateQuestionCallback(props.selectedItem);
            }
        }

        const updateQOption = async (e: any, questionOption: QuestionOption) => {
            const value = e.target?.value;
            const questionToUpdate = props.selectedItem;
            const qOp = questionToUpdate.QuestionOptions.find(qopt => qopt.QuestionOptionID === questionOption.QuestionOptionID);
            if(qOp && value){
                qOp.Label = value;
                qOp.Name = value;
            }

            await updateQuestion(questionToUpdate);
        }

        const deleteQOption = (questionOption: QuestionOption) => {
            const options = props.selectedItem.QuestionOptions;

            const indToDelete = options.findIndex(f => f.QuestionOptionID === questionOption.QuestionOptionID);
            if (indToDelete > -1) {
                const { QuestionID, QuestionOptionID } = questionOption;
                deleteQuestionOption(QuestionID, QuestionOptionID).then(res => {
                    if (res.status === HttpStatusCodeEnum.OK) {
                        options.splice(indToDelete, 1);
                        const payload = {
                            value: props.selectedItem
                        }
                        updateQuestionnaire(QuestionnaireActionTypes.UPDATE_QUESTION, payload);
                    }
                })
            }
        }

        const updatePopOverstatus = (e: React.MouseEvent<HTMLIonButtonElement, MouseEvent>, questionOption: QuestionOption) => {
            setShowPopover({ open: true, questionOption, event: e.nativeEvent })
        }

        const addManualQuestionOption = (e:any) => {
            e.stopPropagation();

            const options = props.selectedItem.QuestionOptions;
            const newOption = new QuestionOption();
            newOption.QuestionID = props.selectedItem.QuestionID;
            newOption.Priority = options?.length > 0 ? options[options.length - 1].Priority + 10 : 10;
            newOption.Name = 'CUS' + options.length + 1;
            newOption.Label = "New Option";

            createQuestionOption(newOption).then(res => {
                if (res.status === HttpStatusCodeEnum.CREATED) {
                    props.selectedItem.QuestionOptions = [...options, res.data.result]
                        .sort((a: QuestionOption, b: QuestionOption) => a.Priority - b.Priority);

                    const payload = {
                        value: props.selectedItem
                    }
                    updateQuestionnaire(QuestionnaireActionTypes.UPDATE_QUESTION, payload);
                }
            })
        }

        const updateContinueToQuestionOption = (e: any, item: QuestionOption) => {
            updateQOption(e, item)
        }

        const reorderOptions = async (event: CustomEvent) => {
            const itemMove = props.selectedItem.QuestionOptions.splice(event.detail.from, 1)[0];
            props.selectedItem.QuestionOptions.splice(event.detail.to, 0, itemMove);
            props.selectedItem.QuestionOptions.forEach((p, index) => {
                p.Priority = (index + 1) * 10;
            })
            event.detail.complete();
            await reorderQuestionOptions(props.selectedItem.QuestionID, props.selectedItem.QuestionOptions);
            updateQuestionnaire(QuestionnaireActionTypes.UPDATE_QUESTION, { value: props.selectedItem });
        }

        const hasInputMode = (val: UIElementEnum) => val === UIElementEnum.tb || val === UIElementEnum.ta;

        const renderQuestionOptions = () => {
            const options = props.selectedItem.QuestionOptions;
            if (options?.length > 0) {
                return (
                    <IonReorderGroup disabled={options.length < 2} onIonItemReorder={reorderOptions}>
                        {options.map(option => 
                            <IonItem key={option.QuestionOptionID}>
                                <IonInput type='text' placeholder="Add Option" onIonBlur={(e) => { updateQOption(e, option) }} value={option.Label} disabled={props.selectedItem.ProcAttribID ? true : false} clearInput></IonInput>
                                <IonButtons className="ion-no-margin" slot="end">
                                    <IonButton size="small" onClick={(e) => { e.stopPropagation(); deleteQOption(option); }}>
                                        <IonIcon slot="icon-only" color="danger" icon={closeCircleOutline} />
                                    </IonButton>
                                </IonButtons>
                                <IonReorder slot="end" />
                            </IonItem>
                        )}
                    </IonReorderGroup>
                )
            }
            else {
                return (
                    <IonItem lines='none'>
                        <IonLabel className="ion-text-center"><b>No Data</b></IonLabel>
                    </IonItem>
                )
            }
        }

        const PopOver = () => {
            return (
                <IonPopover isOpen={showPopover.open} event={showPopover.event} onDidDismiss={() => setShowPopover({ open: false, questionOption: showPopover.questionOption, event: undefined })}>
                    <ContinueTo id="PHQIDIfSelected" item={showPopover.questionOption} currentValue={false} fnToUpdate={updateContinueToQuestionOption} ></ContinueTo>
                </IonPopover>
            )
        }

        useEffect(() => {
           if (props.selectedItem.UIElementID === UIElementEnum.pa){
            setParaValue(props.selectedItem.Label) 
           }
        }, [props.selectedItem.Label, props.selectedItem.UIElementID])

        if (props.selectedItem) {
            return (
                <IonCard>
                    <IonCardHeader color="primary" className="item-header">
                        <IonCardTitle>
                            PROPERTIES
                            {helpMode && <p className="text-right"><b>ID: </b> {props.selectedItem.QuestionID}</p>}
                        </IonCardTitle>
                    </IonCardHeader>
                    <IonCardContent className="ion-no-padding proprties-card-body">
                        <IonGrid className='ion-no-padding'>
                            <IonRow hidden={props.selectedItem.IsLabelHidden} className="ion-padding-end">
                                <IonCol size='12'>
                                    <IonItem lines="inset">
                                        {props.selectedItem.UIElementID === UIElementEnum.pa && (
                                            <ReactQuill 
                                                className="ion-padding" 
                                                theme="snow" 
                                                value={paraValue}
                                                onBlur={updateSelectedQuestionParagraph} 
                                                onChange={setParaValue} />
                                        )}
                                        {props.selectedItem.UIElementID !== UIElementEnum.pa && (
                                            <React.Fragment>
                                                <IonLabel className='input-title' position="stacked">Label</IonLabel>
                                                <IonInput className="input-text-wrap" type='text' id="Label" onIonBlur={(e) => { updateSelectedQuestion(e); e.stopPropagation() }} value={props.selectedItem.Label}></IonInput>
                                            </React.Fragment>
                                        )}
                                    </IonItem>
                                </IonCol>
                            </IonRow>
                            <IonRow className='ion-padding-end'>
                                <IonCol size={hasInputMode(props.selectedItem.UIElementID) ? '6':'12'}>
                                    <IonItem lines="inset">
                                        <IonLabel className='input-title' position="stacked">UI Type</IonLabel>
                                        <IonInput type='text' id="UIElementID" disabled={true} value={UIElementEnum[props.selectedItem.UIElementID]}></IonInput>
                                    </IonItem>
                                </IonCol>
                                {
                                    (hasInputMode(props.selectedItem.UIElementID)) && 
                                    <IonCol size='6'>
                                        <IonItem button detail={false} lines="inset" onClick={
                                                (e: any) => {
                                                    e.persist();
                                                        setShowInputTypePopover({ showPopover: true, event: e })
                                                    }}>
                                            <IonLabel className='input-title' position="stacked">Input Type</IonLabel>
                                            <IonButton fill="clear">
                                                {(props.selectedItem.InputMode || InputModeEnum[InputModeEnum.text]).toUpperCase()}
                                            </IonButton>
                                        </IonItem>
                                </IonCol>
                                }
                            </IonRow>
                            <IonRow className='ion-padding-end'>
                                <IonCol size='6'>
                                    <IonItem lines="inset">
                                        <IonLabel className='input-title' position="stacked">Required</IonLabel>
                                        <IonCheckbox mode="md" id="IsRequired" checked={props.selectedItem.IsRequired} onIonChange={updateCheckbox}></IonCheckbox>
                                    </IonItem>
                                </IonCol>
                                <IonCol size='6'>
                                    <IonItem lines="inset">
                                        <IonLabel className='input-title' position="stacked">Hide Label</IonLabel>
                                        <IonCheckbox mode="md" checked={props.selectedItem.IsLabelHidden} id="IsLabelHidden" onIonChange={updateCheckbox}></IonCheckbox>
                                    </IonItem>
                                </IonCol>
                            </IonRow>
                            <IonRow className='ion-padding-end ion-padding-bottom'>
                                <IonCol size='6'>
                                    <IonItem lines="inset">
                                        <IonLabel className='input-title' position="stacked">Col Span</IonLabel>
                                        <IonInput 
                                            id="ColSpan"
                                            type="number"
                                            min="1" max={sectionColSpan?.toString()}
                                            disabled={props.selectedItem.UIElementID === UIElementEnum.sig}
                                            value={props.selectedItem.ColSpan || sectionColSpan}
                                            onIonBlur={updateColSize} />
                                    </IonItem>
                                </IonCol>
                            </IonRow>
                            <IonRow className="ion-padding-horizontal">
                                <IonCol>
                                    <IonButton expand="full" strong onClick={() => setShowMetaModal(true)}>
                                        <IonIcon icon={createOutline} size="medium" /> EDIT META
                                    </IonButton>
                                </IonCol>
                            </IonRow>

                            <IonModal isOpen={showMetaModal} onDidDismiss={() => setShowMetaModal(false)} cssClass="metadataModal">
                                <IonContent>
                                    {
                                        showJsonEditor && 
                                        <JsonEditor 
                                            value={parsedMeta}
                                            history={true}
                                            allowedModes={['tree', 'text']}
                                            onError={(e:any) => { console.debug(e); setToastMessage("Error rendering JSON"); }} 
                                            onChange={updateParsedMeta}>
                                        </JsonEditor>
                                    }
                                </IonContent>
                                <IonFooter>
                                    <IonToolbar>
                                        <IonButtons>
                                            <IonButton fill="solid" className="w-100" onClick={() => onCancelMetaUpdate() }>Cancel</IonButton>
                                            <IonButton fill="solid" className="w-100" onClick={() => updateMeta()}>Save</IonButton>
                                        </IonButtons>
                                    </IonToolbar>
                                </IonFooter>
                            </IonModal>

                            <IonRow className="ion-padding-horizontal">
                                <IonCol size='12'>
                                    <IonItemDivider className='options-header' color="primary">
                                        <IonTitle>OPTIONS</IonTitle>
                                        <IonButtons slot="end">
                                            {!props.selectedItem.ProcAttribID && ['dd', 'multi', 'rbil', 'rb', 'ms'].includes(UIElementEnum[props.selectedItem.UIElementID]) && (
                                                <IonButton size="small" onClick={addManualQuestionOption}>
                                                    <IonIcon slot="icon-only" icon={addCircle} />
                                                </IonButton>
                                            )}
                                        </IonButtons>
                                    </IonItemDivider>
                                </IonCol>
                            </IonRow>
                            <IonRow>
                                <IonCol size='12'>
                                    <div className='options-list-container'>
                                        {renderQuestionOptions()}
                                    </div>
                                </IonCol>
                            </IonRow>
                        </IonGrid>
                    </IonCardContent>

                    {/* Input type select popover */}
                    <IonPopover 
                        event={inputTypePopoverState.event}
                        isOpen={inputTypePopoverState.showPopover}
                        onDidDismiss={() => setShowInputTypePopover({ showPopover: false, event: undefined })}>
                            <IonList>
                                {Object.keys(InputModeEnum)
                                .filter((x:any) => isNaN(x))
                                .map(key => {
                                    return <IonItem key={key} className="pointer" onClick={_ => updateInputType(key as InputMode)}>{key.toUpperCase()}</IonItem>
                                })}
                            </IonList>
                    </IonPopover>
                    <IonToast 
                        isOpen={showToast}
                        onDidDismiss={() => setShowToast(false)}
                        message={toastMessage}
                        duration={1500} />
                </IonCard>
            )
        }
        else {
            return (<div></div>);
        }

    });

export default PHQProperties;
